import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = ['Identity & License','Whale Threshold','Autonomic Permission','Billing Calibration','HITL Oath & Agreement'];

const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [complete, setComplete] = useState(false);
  const [data, setData] = useState({
    npn: '', carrier: '', carrierName: '', states: ['GA'],
    whaleThreshold: '15000', autonomy: true, notifications: 'weekly',
    tier: 'professional', oath: false, msa_agreed: false
  });

  const canNext = [
    data.npn.length > 4,
    Number(data.whaleThreshold) >= 5000,
    true,
    true,
    data.oath && data.msa_agreed,
  ][step];

  const finish = () => setComplete(true);

  if (complete) return (
    <div className="flex flex-col items-center justify-center min-h-96 space-y-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900">Agency Activated!</h1>
      <p className="text-gray-600 text-center max-w-md">All 13 AI agents are now active and working on your behalf. Your Agent 12 HITL authority is registered. Your MSA agreement is on file.</p>
      <div className="flex gap-3">
        <button onClick={()=>navigate('/agent12')} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Open Agent 12 Dashboard</button>
        <button onClick={()=>navigate('/ai-agents')} className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">View AI Agents</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Agency Onboarding</h1>
        <p className="text-sm text-gray-500 mt-1">Complete all 5 steps to activate your 13-agent workforce</p>
      </div>
      <div className="flex gap-2">
        {STEPS.map((s,i)=>(
          <div key={i} className="flex-1">
            <div className={`h-2 rounded-full transition-colors ${i<step?'bg-blue-600':i===step?'bg-blue-400':'bg-gray-200'}`} />
            <p className={`text-xs mt-1 hidden sm:block ${i===step?'text-blue-600 font-medium':'text-gray-400'}`}>{i+1}. {s}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Step {step+1}: {STEPS[step]}</h2>
        {step===0 && (
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">National Producer Number (NPN)</label>
              <input value={data.npn} onChange={e=>setData(p=>({...p,npn:e.target.value}))} placeholder="Enter your NPN" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Carrier Type</label>
              <select value={data.carrier} onChange={e=>setData(p=>({...p,carrier:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Select...</option><option>Independent</option><option>Captive</option>
              </select></div>
            {data.carrier && <div><label className="block text-sm font-medium text-gray-700 mb-1">Carrier / Agency Name</label>
              <input value={data.carrierName} onChange={e=>setData(p=>({...p,carrierName:e.target.value}))} placeholder="Life & Wealth Solutions" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>}
          </div>
        )}
        {step===1 && (
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Whale Policy Threshold ($)</label>
              <input type="number" value={data.whaleThreshold} onChange={e=>setData(p=>({...p,whaleThreshold:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <p className="text-xs text-gray-500 mt-1">Policies at or above this premium escalate to Agent 12. Minimum: $5,000. Default: $15,000.</p></div>
            <div className="p-3 bg-blue-50 rounded-lg"><p className="text-sm text-blue-700">At ${Number(data.whaleThreshold).toLocaleString()} threshold, My Maestro will flag qualifying policies for your one-tap review before binding.</p></div>
          </div>
        )}
        {step===2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div><p className="text-sm font-medium text-gray-900">Enable Autonomic Self-Healing</p><p className="text-xs text-gray-500">Autonomic Watchdog auto-heals Tier 1 failures</p></div>
              <button onClick={()=>setData(p=>({...p,autonomy:!p.autonomy}))} className={`relative w-11 h-6 rounded-full transition-colors ${data.autonomy?'bg-blue-600':'bg-gray-300'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${data.autonomy?'translate-x-5':''}`}/>
              </button>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Notification Preference</label>
              <select value={data.notifications} onChange={e=>setData(p=>({...p,notifications:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="weekly">Weekly Digest</option><option value="realtime">Real-Time SMS</option><option value="silent">Silent Log Only</option>
              </select></div>
          </div>
        )}
        {step===3 && (
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-2">Select Your Subscription Tier</label>
              {[['foundation','Foundation — $3,500/mo','2,000 validations/mo · Up to 5 agents'],['professional','Professional — $5,500/mo (Recommended)','7,500 validations/mo · Up to 9 agents'],['enterprise','Enterprise — $8,500/mo','15,000 validations/mo · All 13 agents'],].map(([k,label,desc])=>(
                <label key={k} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer mb-2 transition-colors ${data.tier===k?'border-blue-500 bg-blue-50':'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" checked={data.tier===k} onChange={()=>setData(p=>({...p,tier:k}))} className="mt-0.5" />
                  <div><p className="text-sm font-medium text-gray-900">{label}</p><p className="text-xs text-gray-500">{desc}</p></div>
                </label>
              ))}
            </div>
          </div>
        )}
        {step===4 && (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">Human-in-the-Loop Authority Oath</p>
              <p className="text-sm text-blue-800">By accepting, I acknowledge that I hold the required insurance license(s) for the states I have identified. I understand that my digital signature via the platform constitutes my licensed human authority on all policy binding decisions. I accept full professional and legal responsibility for decisions made through this platform under my account.</p>
            </div>

            {/* MSA Agreement Checkbox — required before activation */}
            <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={data.msa_agreed}
                onChange={e=>setData(p=>({...p,msa_agreed:e.target.checked}))}
                className="mt-0.5 w-4 h-4 flex-shrink-0"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Terms of Service</a>,{' '}
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Privacy Policy</a>, and{' '}
                <a href="/msa.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Master Services Agreement</a>.
              </span>
            </label>

            {/* HITL Oath Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
              <input
                type="checkbox"
                checked={data.oath}
                onChange={e=>setData(p=>({...p,oath:e.target.checked}))}
                className="mt-0.5 w-4 h-4 flex-shrink-0"
              />
              <span className="text-sm text-gray-700">I accept the HITL Authority Oath and confirm I am a licensed insurance professional.</span>
            </label>

            {(!data.msa_agreed || !data.oath) && (
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                Both checkboxes are required to activate your agent workforce.
              </p>
            )}
          </div>
        )}
        <div className="flex gap-3 mt-8">
          {step>0 && <button onClick={()=>setStep(s=>s-1)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">← Back</button>}
          {step<4 ? (
            <button onClick={()=>setStep(s=>s+1)} disabled={!canNext} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">Continue →</button>
          ) : (
            <button onClick={finish} disabled={!data.oath || !data.msa_agreed} className="flex-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium">Activate My 13 Agents ✓</button>
          )}
        </div>
      </div>
    </div>
  );
};
export default OnboardingWizard;
